import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useLoaderData, useNavigate, useRevalidator } from 'react-router';
import { CalendarDaysIcon, CheckIcon, PencilSquareIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { deleteGconSession, renameGconSession } from '@/api/gcon';
import { confirm } from '@/components/core/createConfirmation';
import UploadForm from '@/components/gcon/UploadForm';
import { formatDateShort } from '@/utils/miscUtils';

const SessionRow = ({ session, onChanged }) => {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(session.name);

  const saveName = async () => {
    const trimmed = name.trim();
    if (trimmed && trimmed !== session.name) {
      await renameGconSession(session.id, trimmed);
      onChanged();
    }
    setEditing(false);
  };

  const remove = async () => {
    if (await confirm(`Delete "${session.name}"? This cannot be undone.`)) {
      await deleteGconSession(session.id);
      onChanged();
    }
  };

  return (
    <li className="bg-white border border-gray-100 rounded-lg px-4 py-3 flex items-center gap-3 hover:border-gray-300 transition-colors">
      <div className="flex-1 min-w-0">
        {editing ? (
          <form
            className="flex items-center gap-1"
            onSubmit={e => {
              e.preventDefault();
              saveName();
            }}
          >
            <input
              autoFocus
              value={name}
              onChange={e => setName(e.target.value)}
              onKeyDown={e => e.key === 'Escape' && setEditing(false)}
              maxLength={200}
              className="input input-sm input-bordered w-full text-sm"
            />
            <button type="submit" className="btn btn-ghost btn-xs" title="Save">
              <CheckIcon className="w-4 h-4" />
            </button>
            <button type="button" className="btn btn-ghost btn-xs" title="Cancel" onClick={() => setEditing(false)}>
              <XMarkIcon className="w-4 h-4" />
            </button>
          </form>
        ) : (
          <Link
            to={`/gcon/${session.id}`}
            className="text-sm font-semibold text-gray-800 hover:text-teal-600 truncate block"
          >
            {session.name}
          </Link>
        )}
        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1.5">
          <CalendarDaysIcon className="w-3.5 h-3.5" />
          {session.reporting_years.join(', ') || 'No reporting years'}
          <span>·</span>
          {session.output_filenames.length} file pair{session.output_filenames.length === 1 ? '' : 's'}
          <span>·</span>
          Saved {formatDateShort(session.created_at)}
        </p>
      </div>
      {!editing && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            className="btn btn-ghost btn-xs text-gray-400"
            title="Rename"
            onClick={() => {
              setName(session.name);
              setEditing(true);
            }}
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
          <button type="button" className="btn btn-ghost btn-xs text-gray-400" title="Delete" onClick={remove}>
            <TrashIcon className="w-4 h-4" />
          </button>
        </div>
      )}
    </li>
  );
};

SessionRow.propTypes = {
  session: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    reporting_years: PropTypes.arrayOf(PropTypes.number).isRequired,
    output_filenames: PropTypes.arrayOf(PropTypes.string).isRequired,
    created_at: PropTypes.string.isRequired,
  }).isRequired,
  onChanged: PropTypes.func.isRequired,
};

const GconRoute = () => {
  const { sessions } = useLoaderData();
  const navigate = useNavigate();
  const revalidator = useRevalidator();

  const upload = <UploadForm onCreated={session => navigate(`/gcon/${session.id}`)} />;

  if (!sessions.length) {
    return <div className="flex flex-1 min-w-0 h-full bg-gray-50 items-center justify-center p-8">{upload}</div>;
  }

  return (
    <div className="flex flex-1 min-w-0 h-full bg-gray-50 overflow-y-auto">
      <div className="w-full max-w-6xl mx-auto p-8 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,42rem)] gap-6 items-start">
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Saved analyses</h2>
          <ul className="flex flex-col gap-2">
            {sessions.map(session => (
              <SessionRow key={session.id} session={session} onChanged={() => revalidator.revalidate()} />
            ))}
          </ul>
        </section>
        {upload}
      </div>
    </div>
  );
};

export default GconRoute;
